import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { styled } from 'styled-components';
import { toast } from 'react-toastify';
import { Tooltip } from 'react-tooltip';
import { GoogleMap, Rectangle, Circle, Polygon as PolygonShape, Polyline } from '@react-google-maps/api';
import { Feature, MultiPolygon, Polygon } from 'geojson';
import { sanDiegoCoords } from '/imports/gmaps';
import { Checkbox } from '/imports/ui/Checkbox';
import { Icon } from '/imports/ui/Icon';
import { css } from './css';
import { strings } from '/imports/strings';
import { getCcaInfo, getCcaLayer, getCecLayer, listLseRegionsInPolygon } from '/imports/api/lseRegions';
import { listApnsInPolygon } from '/imports/api/apns';
import { listFootprintsInPolygon, geocodeFootprint, staticMapImage } from '/imports/api/buildingFootprints';
import { ZoneLayer, listDacZonesInPolygon, listLmiZonesInPolygon, listMsaZonesInPolygon, lmiThresholdPercent } from '/imports/api/incentiveZones';
import { circuitCapacityMw, listCircuitsInPolygon, listSubstationsInPolygon } from '/imports/api/gridCapacity';
import { formatNumber, roundNumber } from '/imports/utils';

export interface MapLayerLabels {
  /** property to read the label text from on each feature */
  field: string;
  color?: string;
  fontSize?: string;
  fontWeight?: string;
  minZoom?: number;
  /** pixels to move the label down from the feature's anchor, to clear a point marker drawn there */
  offsetY?: number;
}

export type MapLayer =
  | {
      id: string;
      kind: 'geojson';
      visible: boolean;
      /** GeoJSON FeatureCollection or array of features */
      data: any;
      style?: google.maps.Data.StyleOptions | ((feature: google.maps.Data.Feature) => google.maps.Data.StyleOptions);
      onClick?: (e: google.maps.Data.MouseEvent) => void;
      labels?: MapLayerLabels;
      /** Return the content (string or HTML) to show in an InfoWindow on click. */
      popup?: (feature: google.maps.Data.Feature, latLng: google.maps.LatLng) => string | Promise<string | undefined> | undefined;
    }
  | {
      id: string;
      kind: 'imageOverlay';
      visible: boolean;
      /** base64 or blob URL or regular URL */
      image: string;
      bounds: google.maps.LatLngBoundsLiteral | google.maps.LatLngBounds;
      opacity?: number;
      onClick?: (e: google.maps.MapMouseEvent) => void;
      popup?: (latLng: google.maps.LatLng) => string | Promise<string | undefined> | undefined;
    };

const ProspectorMapStyled = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  /* Spinner used inside InfoWindow popups while async content loads. */
  @keyframes prospector-popup-spin { to { transform: rotate(360deg); } }
  /* Hide Google's built-in fullscreen control — we provide our own */
  .gm-fullscreen-control {
    display: none;
  }
  /* Custom fullscreen mode — toggled by FullscreenToggle. Uses pure CSS so
     Google Maps' normal resize handling works (unlike the browser
     Fullscreen API, which was causing projection/overlay offsets). */
  .gmap-fullscreen {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    width: 100vw !important;
    height: 100vh !important;
    z-index: 99;
  }
  .fullscreen-toggle {
    background: ${css.white};
    border: none;
    border-radius: 2px;
    margin: 10px;
    width: 2.25rem;
    height: 2.25rem;
    cursor: pointer;
    font-size: 1.4rem;
    line-height: 1;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
    display: flex;
    align-items: center;
    justify-content: center;
    &:hover {
      background: #f5f5f5;
    }
  }
`;

interface ProspectorMapProps {
  layers?: MapLayer[];
  center?: google.maps.LatLngLiteral;
  zoom?: number;
  mapContainerStyle?: React.CSSProperties;
  children?: React.ReactNode;
  height?: string;
  onMapLoad?: (map: google.maps.Map) => void;
}

/** Google Map wrapper that renders the given layers plus a custom fullscreen toggle. */
export const ProspectorMap = (props: ProspectorMapProps) => {
  const { layers = [], center = sanDiegoCoords, zoom = 9, mapContainerStyle, children, height, onMapLoad } = props;
  const [map, setMap] = useState<google.maps.Map>();
  return (
    <ProspectorMapStyled>
      <GoogleMap
        mapContainerStyle={mapContainerStyle || { width: '100%', minHeight: height || '61.5rem' }}
        center={center}
        zoom={zoom}
        mapTypeId="hybrid"
        options={{ fullscreenControl: false }}
        onLoad={(m) => { setMap(m); onMapLoad?.(m); }}
        onUnmount={() => setMap(undefined)}
      >
        {map && layers.map((layer) => (
          <ProspectorMapLayer key={layer.id} map={map} layer={layer} />
        ))}
        {map && <FullscreenToggle map={map} />}
        {children}
      </GoogleMap>
    </ProspectorMapStyled>
  );
};

/**
 * Maps whose measurement tool is mid-measurement. A left-click there is a vertex of the shape being
 * drawn, so the layers stand down rather than opening a popup over it; both sides live in this file.
 */
const measuringMaps = new Set<google.maps.Map>();

/** Declarative layer component: attaches/detaches overlays based on the `layer` prop */
const ProspectorMapLayer = (props: { map: google.maps.Map, layer: MapLayer }) => {
  const { map, layer } = props;
  const dataRef = useRef<google.maps.Data>(undefined);
  const overlayRef = useRef<google.maps.OverlayView>(undefined);
  const clickListenerRef = useRef<google.maps.MapsEventListener>(undefined);

  useEffect(() => {
    if (!layer.visible) return;
    const infoWindow = new google.maps.InfoWindow();
    const popupListeners: google.maps.MapsEventListener[] = [];
    if (layer.kind === 'geojson') {
      const data = new google.maps.Data();
      data.addGeoJson(layer.data);
      const baseStyle = layer.style;
      data.setStyle((feature) => ({
        ...(typeof baseStyle === 'function' ? baseStyle(feature) : baseStyle),
        clickable: false,
      }));
      data.setMap(map);
      dataRef.current = data;

      const layerOnClick = layer.onClick;
      const popup = layer.popup;
      if (layerOnClick || popup) {
        const rawFeatures: any[] = Array.isArray(layer.data) ? layer.data : (layer.data?.features || []);
        clickListenerRef.current = map.addListener('click', (e: google.maps.MapMouseEvent) => {
          if (!e.latLng || measuringMaps.has(map)) return;
          const lng = e.latLng.lng();
          const lat = e.latLng.lat();
          const tolerance = clickToleranceDegrees(map);
          // Topmost (last-drawn) feature under the cursor wins.
          for (let i = rawFeatures.length - 1; i >= 0; i--) {
            const raw = rawFeatures[i];
            if (!pointInGeometry(lng, lat, raw?.geometry, tolerance)) continue;
            const feature = {
              getProperty: (name: string) => raw?.properties?.[name],
              getId: () => raw?.id,
            } as unknown as google.maps.Data.Feature;
            layerOnClick?.({ feature, latLng: e.latLng } as google.maps.Data.MouseEvent);
            if (popup) showPopup(infoWindow, map, e.latLng, popup(feature, e.latLng));
            break;
          }
        });
      }
      const labelOverlays: google.maps.OverlayView[] = [];
      if (layer.labels) {
        const features = (layer.data?.features || []) as any[];
        for (const feature of features) {
          const text = feature?.properties?.[layer.labels.field];
          if (!text) continue;
          const position = featureAnchorLatLng(feature.geometry);
          if (!position) continue;
          const overlay = createTextOverlay(position, `${text}`, layer.labels);
          overlay.setMap(map);
          labelOverlays.push(overlay);
        }
      }
      return () => {
        clickListenerRef.current?.remove();
        for (const l of popupListeners) l.remove();
        infoWindow.close();
        data.setMap(null);
        dataRef.current = undefined;
        for (const o of labelOverlays) o.setMap(null);
      };
    }
    if (layer.kind === 'imageOverlay') {
      const bounds = layer.bounds instanceof google.maps.LatLngBounds
        ? layer.bounds
        : new google.maps.LatLngBounds(
          { lat: layer.bounds.south, lng: layer.bounds.west },
          { lat: layer.bounds.north, lng: layer.bounds.east }
        );
      const overlay = createImageOverlay(bounds, layer.image, layer.opacity ?? 0.4);
      overlay.setMap(map);
      overlayRef.current = overlay;
      const overlayOnClick = layer.onClick;
      if (overlayOnClick) {
        clickListenerRef.current = map.addListener('click', (e: google.maps.MapMouseEvent) => {
          if (measuringMaps.has(map)) return;
          overlayOnClick(e);
        });
      }
      if (layer.popup) {
        const popup = layer.popup;
        popupListeners.push(map.addListener('click', (e: google.maps.MapMouseEvent) => {
          if (!e.latLng || measuringMaps.has(map)) return;
          showPopup(infoWindow, map, e.latLng, popup(e.latLng));
        }));
      }
      return () => {
        clickListenerRef.current?.remove();
        for (const l of popupListeners) l.remove();
        infoWindow.close();
        overlay.setMap(null);
        overlayRef.current = undefined;
      };
    }
  }, [map, layer]);

  return null;
};

/**
 * Custom OverlayView that pins a raster image to a bounds rect.
 * Must be instantiated after the Google Maps script has loaded (google.maps.OverlayView exists).
 */
const createImageOverlay = (bounds: google.maps.LatLngBounds, image: string, opacity: number) => {
  class ImageOverlay extends google.maps.OverlayView {
    private bounds: google.maps.LatLngBounds;
    private image: string;
    private div?: HTMLDivElement;

    constructor(_bounds: google.maps.LatLngBounds, _image: string) {
      super();
      this.bounds = _bounds;
      this.image = _image;
    }

    onAdd() {
      const div = document.createElement('div');
      div.style.borderStyle = 'none';
      div.style.borderWidth = '0px';
      div.style.position = 'absolute';
      div.style.opacity = `${opacity}`;
      const img = document.createElement('img');
      img.src = this.image;
      img.style.width = '100%';
      img.style.height = '100%';
      img.style.position = 'absolute';
      div.appendChild(img);
      this.div = div;
      this.getPanes()!.overlayLayer.appendChild(div);
    }

    draw() {
      if (!this.div) return;
      const projection = this.getProjection();
      if (!projection) return;
      const sw = projection.fromLatLngToDivPixel(this.bounds.getSouthWest());
      const ne = projection.fromLatLngToDivPixel(this.bounds.getNorthEast());
      if (!sw || !ne) return;
      this.div.style.left = `${sw.x}px`;
      this.div.style.top = `${ne.y}px`;
      this.div.style.width = `${ne.x - sw.x}px`;
      this.div.style.height = `${sw.y - ne.y}px`;
    }

    onRemove() {
      if (this.div) {
        this.div.parentNode?.removeChild(this.div);
        this.div = undefined;
      }
    }
  }
  return new ImageOverlay(bounds, image);
};

/**
 * Finds where a feature's label belongs: a polygon's centroid, a line's middle vertex, or the point itself.
 * @param geometry - GeoJSON geometry object
 * @returns the anchor LatLng, or undefined for a geometry with nothing to anchor to
 */
const featureAnchorLatLng = (geometry: any): google.maps.LatLng | undefined => {
  if (!geometry) return undefined;
  if (geometry.type === 'Point') {
    const [lng, lat] = geometry.coordinates || [];
    return lat === undefined ? undefined : new google.maps.LatLng(lat, lng);
  }
  if (geometry.type === 'LineString' || geometry.type === 'MultiLineString') {
    const line = geometry.type === 'LineString' ? geometry.coordinates : geometry.coordinates?.[0];
    const vertex = line?.[Math.floor((line.length || 0) / 2)];
    return vertex ? new google.maps.LatLng(vertex[1], vertex[0]) : undefined;
  }
  let ring: number[][] | undefined;
  if (geometry.type === 'Polygon') ring = geometry.coordinates?.[0];
  else if (geometry.type === 'MultiPolygon') {
    // pick the outer ring of the largest polygon (by vertex count)
    const polys: number[][][] = (geometry.coordinates || []).map((p: any) => p?.[0]).filter(Boolean);
    ring = polys.sort((a, b) => b.length - a.length)[0];
  }
  if (!ring?.length) return undefined;
  const n = ring.length > 1 && ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1]
    ? ring.length - 1 : ring.length;
  let sx = 0, sy = 0;
  for (let i = 0; i < n; i++) { sx += ring[i][0]; sy += ring[i][1]; }
  return new google.maps.LatLng(sy / n, sx / n);
};

const POPUP_LOADING_HTML = /*html*/`
  <div style="padding:4px 4px 6px 4px;display:flex;align-items:center;gap:8px">
    <div style="width:14px;height:14px;border:2px solid #ccc;border-top-color:#333;border-radius:50%;animation:prospector-popup-spin 0.8s linear infinite"></div>
    <span>${strings.Loading()}…</span>
  </div>
`;

/**
 * Opens an InfoWindow with popup content, showing a spinner while a promised result resolves.
 * @param infoWindow - shared InfoWindow instance
 * @param map - map to open on
 * @param latLng - position to anchor the popup
 * @param result - content string, a promise of one, or undefined to skip
 */
const showPopup = (
  infoWindow: google.maps.InfoWindow,
  map: google.maps.Map,
  latLng: google.maps.LatLng,
  result: string | Promise<string | undefined> | undefined,
) => {
  if (result && typeof (result as Promise<any>).then === 'function') {
    infoWindow.setContent(POPUP_LOADING_HTML);
    infoWindow.setPosition(latLng);
    infoWindow.open({ map });
    (result as Promise<string | undefined>).then((content) => {
      if (!content) { infoWindow.close(); return; }
      infoWindow.setContent(content);
    });
    return;
  }
  if (!result) return;
  infoWindow.setContent(result as string);
  infoWindow.setPosition(latLng);
  infoWindow.open({ map });
};

/**
 * Builds an OverlayView that renders a text label at a map position.
 * @param position - LatLng to anchor the label
 * @param text - label text
 * @param labels - styling and minZoom options
 * @returns an unattached TextOverlay instance
 */
const createTextOverlay = (position: google.maps.LatLng, text: string, labels: MapLayerLabels) => {
  class TextOverlay extends google.maps.OverlayView {
    private div?: HTMLDivElement;
    onAdd() {
      const div = document.createElement('div');
      div.textContent = text;
      div.style.position = 'absolute';
      div.style.color = labels.color || '#4a148c';
      div.style.fontSize = labels.fontSize || '11px';
      div.style.fontWeight = labels.fontWeight || 'bold';
      div.style.whiteSpace = 'nowrap';
      div.style.pointerEvents = 'none';
      div.style.transform = 'translate(-50%, -50%)';
      div.style.textShadow = '0 0 2px #fff, 0 0 3px #fff, 0 0 4px #fff';
      this.div = div;
      this.getPanes()!.overlayLayer.appendChild(div);
    }
    draw() {
      if (!this.div) return;
      const projection = this.getProjection();
      if (!projection) return;
      const point = projection.fromLatLngToDivPixel(position);
      if (!point) return;
      if (labels.minZoom !== undefined) {
        const zoom = (this.getMap() as google.maps.Map | undefined)?.getZoom();
        this.div.style.display = zoom !== undefined && zoom >= labels.minZoom ? '' : 'none';
      }
      this.div.style.left = `${point.x}px`;
      this.div.style.top = `${point.y + (labels.offsetY || 0)}px`;
    }
    onRemove() {
      if (this.div) { this.div.parentNode?.removeChild(this.div); this.div = undefined; }
    }
  }
  return new TextOverlay();
};

/**
 * Definition of a layer that the Prospector page knows about.
 * `fetch` is called the first time the user toggles the layer on;
 * the result is cached so subsequent toggles are instant.
 */
type MapLayerFetchResult = MapLayer extends infer L
  ? L extends MapLayer ? Omit<L, 'id' | 'visible' | 'kind'> : never
  : never;

export interface ProspectorLayerDef {
  id: string;
  label: string;
  kind: 'geojson' | 'imageOverlay';
  requiresPolygon?: boolean;
  /** if set, the layer can only be enabled when the map's zoom is at or beyond this level */
  minZoom?: number;
  minZoomWarning?: string;
  fetch: (polygon?: Polygon, dims?: { width: number, height: number }) => Promise<MapLayerFetchResult>;
}

/**
 * Takes a layer's features, warning when the publisher sent only part of what the viewport covers, or when
 * a source behind the layer could not be read and what is shown lacks what it would have added.
 * @param layer - the publisher's answer
 * @param label - the layer's name, for the warning
 * @returns the features
 */
const featuresOf = <T,>(layer: ZoneLayer<T>, label: string) => {
  if (layer.truncated) toast.warning(strings.ShowingPartOfLayer(label));
  if (layer.partial) toast.warning(strings.LayerPartlyUnavailable(label));
  return layer.features;
};

/** The circuit's customer mix as a popup line, dropped where the utility publishes none. */
const customerRow = (customers: unknown) => {
  const counts = (customers || {}) as { residential?: number, commercial?: number, industrial?: number, agricultural?: number };
  const parts = [
    [strings.Residential(), counts.residential], [strings.Commercial(), counts.commercial],
    [strings.Industrial(), counts.industrial], [strings.Agricultural(), counts.agricultural],
  ].filter(([, count]) => typeof count === 'number') as [string, number][];
  if (!parts.length) return undefined;
  return labelled(strings.Customers(), parts.map(([label, share]) => `${strings.Percentage(formatNumber(share))} ${label.toLowerCase()}`).join(', '));
};

const htmlEscapes: { [character: string]: string } = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' };

/**
 * Escapes a value published by an outside service for the popup's markup.
 * @param value - the published value
 * @returns the escaped text
 */
const escapeHtml = (value: string) => `${value}`.replace(/[&<>"]/g, (character) => htmlEscapes[character]);

/**
 * Renders a popup: a bold heading over one line per figure, dropping the figures a feature lacks.
 * @param title - the heading
 * @param rows - the lines, in order
 * @returns the popup markup
 */
const popupHtml = (title: string, rows: (string | undefined)[]) =>
  `<div style="font-weight:bold">${escapeHtml(title)}</div>`
  + rows.filter(Boolean).map((row) => `<div>${escapeHtml(row as string)}</div>`).join('');

/**
 * Pairs a label with a value for one popup line.
 * @param label - the field's name
 * @param value - the value
 * @returns the line
 */
const labelled = (label: string, value: string) => `${label}: ${value}`;

/** As `labelled`, for a value a feature may not carry. */
const labelledIf = (label: string, value?: string) => value ? labelled(label, value) : undefined;

/** A megawatt figure as a popup line, dropped where the publisher states none. */
const megawattRow = (label: string, value: unknown) =>
  typeof value === 'number' ? labelled(label, strings.Megawatts(formatNumber(value, 2))) : undefined;

/** A percentage figure as a popup line, dropped where the publisher states none. */
const percentRow = (label: string, value: unknown) =>
  typeof value === 'number' ? labelled(label, strings.Percentage(formatNumber(value, 1))) : undefined;

// The burden categories a CEJST tract can be identified under, each a flag on the tract.
const dacBurdens = [
  { 'field': 'N_CLT', 'label': strings.DacCategoryClimate },
  { 'field': 'N_ENY', 'label': strings.DacCategoryEnergy },
  { 'field': 'N_HLTH', 'label': strings.DacCategoryHealth },
  { 'field': 'N_HSG', 'label': strings.DacCategoryHousing },
  { 'field': 'N_PLN', 'label': strings.DacCategoryLegacyPollution },
  { 'field': 'N_TRN', 'label': strings.DacCategoryTransportation },
  { 'field': 'N_WKFC', 'label': strings.DacCategoryWorkforce },
  { 'field': 'N_WTR', 'label': strings.DacCategoryWater },
];

/**
 * A parcel's area on the sphere, holes excluded. The parcel services' own Shape__Area is not used: the
 * statewide and SANDAG layers are merged, and their area fields are not in the same units.
 * @param geometry - the parcel's GeoJSON geometry
 * @returns the area in square meters, or undefined for a geometry that is not a polygon
 */
const parcelAreaSquareMeters = (geometry?: Polygon | MultiPolygon) => {
  const ringArea = (ring: number[][]) => google.maps.geometry.spherical.computeArea(ring.map(([lng, lat]) => ({ lat, lng })));
  const polygonArea = (rings: number[][][]) => ringArea(rings[0]) - rings.slice(1).reduce((total, hole) => total + ringArea(hole), 0);
  if (geometry?.type === 'Polygon') return polygonArea(geometry.coordinates);
  if (geometry?.type === 'MultiPolygon') return geometry.coordinates.reduce((total, polygon) => total + polygonArea(polygon), 0);
  return undefined;
};

/**
 * Registry of available layers. Add new layers here — the hook will
 * automatically handle fetching, caching, toggle state, and passing them
 * into the ProspectorMap as MapLayer objects.
 */
const LAYER_DEFS: ProspectorLayerDef[] = [
  // {
  //   id: 'dac',
  //   label: 'Disadvantaged Communities',
  //   kind: 'geojson',
  //   fetch: async () => {
  //     const data = await fetchDacLayer.call();
  //     return { data, style: { fillColor: 'blue', fillOpacity: 0.3, strokeColor: 'blue' } };
  //   },
  // },
  // // Example image overlay layer:
  // {
  //   id: 'slope',
  //   label: 'Slope',
  //   kind: 'imageOverlay',
  //   fetch: async () => {
  //     const { image, bounds } = await fetchSlopeLayer.call();
  //     return { image, bounds, opacity: 0.5 };
  //   },
  // },
  {
    id: 'utilities',
    label: strings.Utilities(),
    kind: 'geojson',
    requiresPolygon: true,
    fetch: async (polygon) => {
      if (!polygon) throw new Error('utilities layer requires a polygon');
      const features = await listLseRegionsInPolygon.call({ polygon });
      return {
        data: { type: 'FeatureCollection', features } as any,
        style: { fillColor: 'purple', fillOpacity: 0.35, strokeColor: 'purple', strokeOpacity: 0.9, strokeWeight: 1 },
        labels: { field: 'NAME', color: '#4a148c', fontWeight: 'bold' },
        popup: (feature: google.maps.Data.Feature) => {
          const name = feature.getProperty('NAME');
          const type = feature.getProperty('TYPE');
          if (!name) return undefined;
          return `<div style="font-weight:bold">${name}</div>${type ? `<div>${type}</div>` : ''}`;
        },
      };
    },
  },
  {
    id: 'apns',
    label: strings.Apns(),
    kind: 'geojson',
    requiresPolygon: true,
    minZoom: 14,
    minZoomWarning: strings.PleaseZoomInFurtherToEnableTheApnLayer(),
    fetch: async (polygon) => {
      if (!polygon) throw new Error('apns layer requires a polygon');
      const apns = await listApnsInPolygon.call({ polygon });
      // The popup sees only a feature's properties, so the area is measured here and carried on them.
      const features = apns.map((apn) => ({ ...apn, properties: { ...apn.properties, areaSquareMeters: parcelAreaSquareMeters(apn.geometry) } }));
      return {
        data: { type: 'FeatureCollection', features } as any,
        style: { fillColor: 'orange', fillOpacity: 0.2, strokeColor: 'orange', strokeOpacity: 0.9, strokeWeight: 1 },
        labels: { field: 'apn', color: '#e65100', fontWeight: 'bold', minZoom: 19 },
        popup: (feature: google.maps.Data.Feature) => {
          const apn = feature.getProperty('apn') || feature.getProperty('PARCEL_APN');
          if (!apn) return undefined;
          const address = feature.getProperty('SITE_ADDR') || feature.getProperty('FullStreetAddress');
          const areaSquareMeters = feature.getProperty('areaSquareMeters');
          const area = typeof areaSquareMeters === 'number'
            ? strings.MeasurementArea(formatNumber(areaSquareMeters * SQUARE_FEET_PER_SQUARE_METER), formatNumber(areaSquareMeters * ACRES_PER_SQUARE_METER, 2))
            : undefined;
          return `<div style="font-weight:bold">${apn}</div>${address ? `<div>${address}</div>` : ''}${area ? `<div>${area}</div>` : ''}`;
        },
      };
    },
  },
  {
    // Click handler is wired in useProspectorLayers (needs hook state for the highlight overlay).
    id: 'buildingFootprints',
    label: strings.Footprints(),
    kind: 'geojson',
    requiresPolygon: true,
    minZoom: 15,
    fetch: async (polygon) => {
      if (!polygon) throw new Error('footprints layer requires a polygon');
      const features = await listFootprintsInPolygon.call({ polygon });
      // Copy area/centroid into properties; addGeoJson drops non-standard top-level Feature fields.
      const withProps = features.map((f) => ({ ...f, properties: { ...f.properties, area: f.area, centroid: f.centroid } }));
      return {
        data: { type: 'FeatureCollection', features: withProps } as any,
        style: { fillColor: '#00c853', fillOpacity: 0.3, strokeColor: '#00c853', strokeOpacity: 0.9, strokeWeight: 1 },
        popup: async (feature: google.maps.Data.Feature, latLng: google.maps.LatLng) => {
          const area = feature.getProperty('area') as number | undefined;
          const centroid = feature.getProperty('centroid') as number[] | undefined;
          const [lng, lat] = centroid ?? [latLng.lng(), latLng.lat()];
          const [enrichment, image] = await Promise.all([
            geocodeFootprint.call({ centroid: [lng, lat] }),
            staticMapImage.call({ lat, lng }),
          ]);
          const address = enrichment?.addressString;
          if (!address && area === undefined && !image) return undefined;
          return `${address ? `<div style="font-weight:bold">${address}</div>` : ''}`
            + `${area !== undefined ? `<div>${roundNumber(area)} ft²</div>` : ''}`
            + `${image ? `<img src="${image}" style="width:280px;height:auto;display:block;margin-top:6px;border-radius:4px" />` : ''}`;
        },
      };
    },
  },
  {
    id: 'ccas',
    label: strings.CalCca(),
    kind: 'imageOverlay',
    requiresPolygon: true,
    fetch: async (polygon, dims) => {
      if (!polygon) throw new Error('ccas layer requires a polygon');
      const width = dims?.width || 1024;
      const height = dims?.height || 1024;
      const result = await getCcaLayer.call({ polygon, width, height });
      if (!result) throw new Error('ccas layer fetch failed');
      return {
        image: result.image,
        bounds: result.bounds,
        opacity: 1,
        popup: async (latLng: google.maps.LatLng) => {
          const info = await getCcaInfo.call({ lat: latLng.lat(), lng: latLng.lng() });
          const name = info?.['Utility Name'] || info?.['Name Shorthand'] || info?.retl_ut;
          if (!name) return undefined;
          const type = info?.['Utility Type'] || info?.type;
          return `<div style="font-weight:bold">${name}</div>${type ? `<div>${type}</div>` : ''}`;
        },
      };
    },
  },
  {
    id: 'cecCca',
    label: strings.CecCca(),
    kind: 'geojson',
    requiresPolygon: true,
    fetch: async (polygon) => {
      if (!polygon) throw new Error('cec cca layer requires a polygon');
      const features = await getCecLayer.call({ polygon });
      return {
        data: { type: 'FeatureCollection', features } as any,
        style: { fillColor: '#00897b', fillOpacity: 0.3, strokeColor: '#00897b', strokeOpacity: 0.9, strokeWeight: 1 },
        labels: { field: 'Utility', color: '#004d40', fontWeight: 'bold' },
        popup: (feature: google.maps.Data.Feature) => {
          const utility = feature.getProperty('Utility') as string | undefined;
          if (!utility) return undefined;
          const type = feature.getProperty('Type') as string | undefined;
          const phone = feature.getProperty('Phone') as string | undefined;
          const url = feature.getProperty('URL') as string | undefined;
          return `<div style="font-weight:bold">${utility}</div>`
            + `${type ? `<div>${type}</div>` : ''}`
            + `${phone ? `<div>${phone}</div>` : ''}`
            + `${url ? `<div><a href="${url}" target="_blank">${url}</a></div>` : ''}`;
        },
      };
    },
  },
  {
    id: 'lmi',
    label: strings.Lmi(),
    kind: 'geojson',
    requiresPolygon: true,
    fetch: async (polygon) => {
      if (!polygon) throw new Error('lmi layer requires a polygon');
      const features = featuresOf(await listLmiZonesInPolygon.call({ polygon }), strings.Lmi());
      return {
        data: { type: 'FeatureCollection', features } as any,
        style: (feature: google.maps.Data.Feature) => {
          const color = Number(feature.getProperty('LOWMODPCT') || 0) >= lmiThresholdPercent ? '#d50000' : '#00c853';
          return { fillColor: color, fillOpacity: 0.12, strokeColor: color, strokeOpacity: 0.8, strokeWeight: 1 };
        },
        popup: (feature: google.maps.Data.Feature) => {
          const percent = feature.getProperty('LOWMODPCT') as number | undefined;
          if (percent === undefined) return undefined;
          const people = feature.getProperty('LOWMOD') as number | undefined;
          const population = feature.getProperty('LOWMODUNIV') as number | undefined;
          return popupHtml(strings.LmiShareOfPopulation(formatNumber(percent, 1)), [
            people === undefined || population === undefined
              ? undefined
              : strings.LmiPeople(formatNumber(people), formatNumber(population)),
          ]);
        },
      };
    },
  },
  {
    id: 'dac',
    label: strings.Dac(),
    kind: 'geojson',
    requiresPolygon: true,
    fetch: async (polygon) => {
      if (!polygon) throw new Error('dac layer requires a polygon');
      const features = featuresOf(await listDacZonesInPolygon.call({ polygon }), strings.Dac());
      return {
        data: { type: 'FeatureCollection', features } as any,
        style: { fillColor: '#2962ff', fillOpacity: 0.18, strokeColor: '#2962ff', strokeOpacity: 0.8, strokeWeight: 1 },
        popup: (feature: google.maps.Data.Feature) => {
          const burdens = dacBurdens.filter((burden) => Number(feature.getProperty(burden.field)) === 1);
          const county = feature.getProperty('CF') as string | undefined;
          const state = feature.getProperty('SF') as string | undefined;
          return popupHtml(strings.DisadvantagedCommunity(), [
            [county, state].filter(Boolean).join(', ') || undefined,
            burdens.length ? labelled(strings.DacBurdens(), burdens.map((burden) => burden.label()).join(', ')) : undefined,
          ]);
        },
      };
    },
  },
  {
    id: 'msa',
    label: strings.Msa(),
    kind: 'geojson',
    requiresPolygon: true,
    fetch: async (polygon) => {
      if (!polygon) throw new Error('msa layer requires a polygon');
      const features = featuresOf(await listMsaZonesInPolygon.call({ polygon }), strings.Msa());
      const qualifies = (feature: google.maps.Data.Feature, field: string) => feature.getProperty(field) === 'Yes';
      return {
        data: { type: 'FeatureCollection', features } as any,
        style: (feature: google.maps.Data.Feature) => {
          const energy = qualifies(feature, 'ec_qual_status');
          const fossil = qualifies(feature, 'fee_qual_status');
          const color = energy && fossil ? '#ff6d00' : energy ? '#00b8d4' : fossil ? '#d500f9' : '#9e9e9e';
          return { fillColor: color, fillOpacity: 0.12, strokeColor: color, strokeOpacity: 0.8, strokeWeight: 1 };
        },
        popup: (feature: google.maps.Data.Feature) => {
          const area = feature.getProperty('msa_area_name') as string | undefined;
          if (!area) return undefined;
          const yesNo = (field: string) => qualifies(feature, field) ? strings.Yes() : strings.No();
          return popupHtml(area, [
            feature.getProperty('msa_nmsa_label') as string | undefined,
            labelled(strings.EnergyCommunity(), yesNo('ec_qual_status')),
            labelled(strings.FossilFuelEmployment(), yesNo('fee_qual_status')),
          ]);
        },
      };
    },
  },
  {
    id: 'substations',
    label: strings.Substations(),
    kind: 'geojson',
    requiresPolygon: true,
    minZoom: 8,
    fetch: async (polygon) => {
      if (!polygon) throw new Error('substations layer requires a polygon');
      const features = featuresOf(await listSubstationsInPolygon.call({ polygon }), strings.Substations());
      return {
        data: { type: 'FeatureCollection', features } as any,
        // Near-black with a white ring: no other layer or marker uses it, and it holds on both map and satellite.
        style: {
          fillColor: '#212121', fillOpacity: 0.25, strokeColor: '#212121', strokeOpacity: 0.9, strokeWeight: 2,
          icon: {
            path: google.maps.SymbolPath.CIRCLE, scale: 10,
            fillColor: '#212121', fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 3,
          },
        },
        labels: { field: 'name', color: '#212121', fontWeight: 'bold', minZoom: 12, offsetY: 20 },
        popup: (feature: google.maps.Data.Feature) => {
          const name = feature.getProperty('name') as string | undefined;
          if (!name) return undefined;
          const voltage = feature.getProperty('voltage') as string | undefined;
          return popupHtml(voltage ? `${name} (${voltage})` : name, [
            megawattRow(strings.ExistingGeneration(), feature.getProperty('existingGeneration')),
            megawattRow(strings.QueuedGeneration(), feature.getProperty('queuedGeneration')),
            megawattRow(strings.ProjectedLoad(), feature.getProperty('projectedLoad')),
            percentRow(strings.PenetrationLevel(), feature.getProperty('penetration')),
            megawattRow(strings.RemainingCapacity(), feature.getProperty('remainingCapacity')),
            feature.getProperty('note') as string | undefined,
          ]);
        },
      };
    },
  },
  {
    id: 'circuits',
    label: strings.Circuits(),
    kind: 'geojson',
    requiresPolygon: true,
    minZoom: 14,
    fetch: async (polygon) => {
      if (!polygon) throw new Error('circuits layer requires a polygon');
      const features = featuresOf(await listCircuitsInPolygon.call({ polygon }), strings.Circuits());
      return {
        data: { type: 'FeatureCollection', features } as any,
        style: (feature: google.maps.Data.Feature) => {
          const capacity = Number(feature.getProperty('icaPv') ?? 0);
          const color = capacity >= circuitCapacityMw.roomy ? '#00c853'
            : capacity >= circuitCapacityMw.tight ? '#ffab00' : '#d50000';
          return { strokeColor: color, strokeOpacity: 0.9, strokeWeight: 3, fillColor: color, fillOpacity: 0.4 };
        },
        popup: (feature: google.maps.Data.Feature) => {
          const circuit = feature.getProperty('circuit') as string | undefined;
          const voltage = feature.getProperty('voltage') as string | undefined;
          if (!circuit) return undefined;
          return popupHtml(voltage ? `${circuit} (${voltage})` : circuit, [
            labelledIf(strings.Substation(), feature.getProperty('substation') as string | undefined),
            megawattRow(strings.PvCapacity(), feature.getProperty('icaPv')),
            megawattRow(strings.PvCapacityWithCurtailment(), feature.getProperty('icaPvOpFlex')),
            megawattRow(strings.LoadCapacity(), feature.getProperty('icaLoad')),
            labelledIf(strings.MostLimitingFactor(), feature.getProperty('limitingFactor') as string | undefined),
            megawattRow(strings.HeadroomEstimate(), feature.getProperty('headroom')),
            megawattRow(strings.ConnectedGeneration(), feature.getProperty('connectedGeneration')),
            megawattRow(strings.QueuedGeneration(), feature.getProperty('queuedGeneration')),
            megawattRow(strings.TotalGeneration(), feature.getProperty('totalGeneration')),
            customerRow(feature.getProperty('customers')),
          ]);
        },
      };
    },
  },
];

/**
 * Hook that owns all layer state (visibility, fetched data cache).
 * Returns the MapLayer array ready to pass to <ProspectorMap />
 * plus a <LayerToggles /> element that renders the toggle UI.
 */
export const useProspectorLayers = (map?: google.maps.Map) => {
  const [visibleIds, setVisibleIds] = useState<string[]>([]);
  const [loadingIds, setLoadingIds] = useState<string[]>([]);
  const [fetchedData, setFetchedData] = useState<{ [id: string]: any }>({});
  const [polygon, setPolygon] = useState<Polygon>();
  const polygonKey = polygon ? JSON.stringify(polygon.coordinates) : '';
  const [gmapsFootprint, setGmapsFootprint] = useState<Feature | null>(null);

  useEffect(() => {
    if (!map) return;
    const updateViewport = () => {
      const bounds = map.getBounds();
      if (!bounds) return;
      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      setPolygon({
        type: 'Polygon',
        coordinates: [[
          [sw.lng(), sw.lat()],
          [ne.lng(), sw.lat()],
          [ne.lng(), ne.lat()],
          [sw.lng(), ne.lat()],
          [sw.lng(), sw.lat()],
        ]],
      });
    };
    updateViewport();
    const listener = map.addListener('idle', updateViewport);
    return () => listener.remove();
  }, [map]);

  const fetchLayer = useCallback(async (id: string) => {
    const def = LAYER_DEFS.find((d) => d.id === id);
    if (!def) return;
    if (def.requiresPolygon && !polygon) return;
    const div = map?.getDiv();
    const dims = div ? { width: div.offsetWidth, height: div.offsetHeight } : undefined;
    setLoadingIds((ids) => [...ids, id]);
    try {
      const data = await def.fetch(polygon, dims);
      setFetchedData((prev) => ({ ...prev, [id]: data }));
    }
    catch (error) {
      // Layers are read from their publishers on the spot, so one that will not load is news: say so and
      // turn it back off, rather than leaving a ticked box with nothing under it.
      console.error('Failed to fetch layer', id, error);
      toast.error(strings.CouldNotLoadLayer(def.label));
      setVisibleIds((ids) => ids.filter((x) => x !== id));
    }
    finally {
      setLoadingIds((ids) => ids.filter((x) => x !== id));
    }
  }, [polygonKey, map]);

  const toggle = useCallback(async (id: string, checked: boolean) => {
    if (!checked) {
      setVisibleIds((ids) => ids.filter((x) => x !== id));
      return;
    }
    const def = LAYER_DEFS.find((d) => d.id === id);
    if (def?.minZoom !== undefined && (map?.getZoom() ?? 0) < def.minZoom) {
      toast.warning(def.minZoomWarning ?? strings.PleaseZoomInFurtherToEnableThisLayer());
      return;
    }
    setVisibleIds((ids) => ids.includes(id) ? ids : [...ids, id]);
    if (def?.requiresPolygon || !fetchedData[id]) await fetchLayer(id);
  }, [fetchedData, fetchLayer, map]);

  const reload = useCallback(() => {
    for (const def of LAYER_DEFS) {
      if (!def.requiresPolygon) continue;
      if (!visibleIds.includes(def.id)) continue;
      if (!polygon) continue;
      if (def.minZoom !== undefined && (map?.getZoom() ?? 0) < def.minZoom) continue;
      fetchLayer(def.id);
    }
  }, [visibleIds, polygonKey, fetchLayer, map]);

  // Enter refetches visible viewport-scoped layers; pan/zoom intentionally does not auto-refetch.
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
      reload();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [reload]);


  /**
   * Reverse-geocodes a clicked footprint and stores Google's displayPolygon as the highlight overlay.
   * @param e - map click event
   */
  const onFootprintClick = useCallback(async (e: google.maps.Data.MouseEvent) => {
    if (!e.latLng) return;
    const enrichment = await geocodeFootprint.call({ centroid: [e.latLng.lng(), e.latLng.lat()] });
    if (!enrichment?.displayPolygon) {
      toast.info(strings.NoGoogleFootprintForThisBuilding());
      return;
    }
    setGmapsFootprint({
      type: 'Feature',
      properties: {
        displayName: enrichment.displayName,
        primaryType: enrichment.primaryType,
        footprintArea: enrichment.footprintArea,
        addressString: enrichment.addressString,
      },
      geometry: enrichment.displayPolygon,
    });
  }, []);

  // Deps exclude gmapsFootprint: a footprint click must not change layer identity, else its effect tears down the InfoWindow it just opened ("click twice" bug).
  const registryLayers = useMemo<MapLayer[]>(() => {
    return LAYER_DEFS
      .filter((def) => visibleIds.includes(def.id) && fetchedData[def.id])
      .map((def) => {
        const layer = { id: def.id, kind: def.kind, visible: true, ...fetchedData[def.id] } as MapLayer;
        if (def.id === 'buildingFootprints' && layer.kind === 'geojson') layer.onClick = onFootprintClick;
        return layer;
      });
  }, [visibleIds, fetchedData, onFootprintClick]);

  const highlightLayer = useMemo<MapLayer | null>(() => {
    if (!gmapsFootprint) return null;
    return {
      id: 'gmapsFootprintHighlight',
      kind: 'geojson',
      visible: true,
      data: { type: 'FeatureCollection', features: [gmapsFootprint] },
      style: { fillColor: '#1a73e8', fillOpacity: 0.35, strokeColor: '#1a73e8', strokeOpacity: 1, strokeWeight: 2 },
      popup: (feature: google.maps.Data.Feature) => {
        const name = feature.getProperty('displayName') as string | undefined;
        const type = feature.getProperty('primaryType') as string | undefined;
        const area = feature.getProperty('footprintArea') as number | undefined;
        const address = feature.getProperty('addressString') as string | undefined;
        const header = name || address;
        if (!header) return undefined;
        return `<div style="font-weight:bold">${header}</div>`
          + `${type ? `<div>${type}</div>` : ''}`
          + `${area ? `<div>${roundNumber(area)} ft²</div>` : ''}`;
      },
    };
  }, [gmapsFootprint]);

  const layers = useMemo<MapLayer[]>(
    () => (highlightLayer ? [...registryLayers, highlightLayer] : registryLayers),
    [registryLayers, highlightLayer],
  );

  const toggles = (
    <MapControl map={map} position="TOP_LEFT">
      <LayerToggles
        defs={LAYER_DEFS}
        visibleIds={visibleIds}
        loadingIds={loadingIds}
        onToggle={toggle}
      />
    </MapControl>
  );

  return { layers, toggles, loading: loadingIds.length > 0 };
};

/**
 * Portals React children into `map.controls[google.maps.ControlPosition[position]]`.
 * Keeps positioning to Google Maps while the content is authored as regular JSX.
 */
const MapControl = (props: {
  map?: google.maps.Map,
  position: keyof typeof google.maps.ControlPosition,
  children: React.ReactNode,
}) => {
  const { map, position, children } = props;
  const container = useMemo(() => typeof document !== 'undefined' ? document.createElement('div') : null, []);
  useEffect(() => {
    if (!map || !container) return;
    const controls = map.controls[google.maps.ControlPosition[position]];
    controls.push(container);
    return () => {
      for (let i = 0; i < controls.getLength(); i++) {
        if (controls.getAt(i) === container) { controls.removeAt(i); break; }
      }
    };
  }, [map, position, container]);
  if (!map || !container) return null;
  return createPortal(children, container);
};

/**
 * Fullscreen toggle that swaps a CSS class on the map div.
 * Avoids the browser Fullscreen API, which drifts Google Maps' projection (CCA image shifts eastward).
 */
const FullscreenToggle = ({ map }: { map: google.maps.Map }) => {
  const [fullscreen, setFullscreen] = useState(false);
  const toggle = () => {
    const div = map.getDiv() as HTMLElement;
    if (fullscreen) {
      div.classList.remove('gmap-fullscreen');
    }
    else {
      div.classList.add('gmap-fullscreen');
    }
    setFullscreen((v) => !v);
    // Force Google Maps to re-measure the resized container.
    setTimeout(() => google.maps.event.trigger(map, 'resize'), 0);
  };
  return (
    <MapControl map={map} position="RIGHT_TOP">
      <button
        className="fullscreen-toggle"
        aria-label={fullscreen ? strings.ExitFullscreen() : strings.Fullscreen()}
        data-tooltip-id="fullscreen-toggle-tip"
        data-tooltip-content={fullscreen ? strings.ExitFullscreen() : strings.Fullscreen()}
        data-tooltip-place="left"
        onClick={toggle}
      >{fullscreen ? '⤓' : '⛶'}</button>
      <Tooltip id="fullscreen-toggle-tip" />
    </MapControl>
  );
};

const LayerTogglesStyled = styled.div`
  margin: 10px;
  .layers-button {
    background: ${css.white};
    border: none;
    border-radius: 2px;
    padding: 10px 1rem;
    cursor: pointer;
    font-size: 1.05rem;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    &:hover {
      background: rgba(255, 255, 255, 1);
    }
  }
  .layers-panel {
    margin-top: 1px;
    background: rgba(255, 255, 255, 0.95);
    border-radius: 2px;
    padding: 0.75rem 1rem;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
    label {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      cursor: pointer;
      margin: 0.25rem 0;
      white-space: nowrap;
      font-size: 1rem;
    }
    .loading {
      font-style: italic;
      opacity: 0.6;
      margin-left: 0.25rem;
    }
  }
`;

/** Collapsible panel of checkboxes for toggling map layers on and off. */
const LayerToggles = (props: {
  defs: ProspectorLayerDef[],
  visibleIds: string[],
  loadingIds: string[],
  onToggle: (id: string, checked: boolean) => void,
}) => {
  const { defs, visibleIds, loadingIds, onToggle } = props;
  const [open, setOpen] = useState(false);
  if (!defs.length) return null;
  return (
    <LayerTogglesStyled>
      <button className="layers-button" onClick={() => setOpen(!open)}>
        {strings.Layers()} {visibleIds.length > 0 && `(${visibleIds.length})`} {open ? '▲' : '▼'}
      </button>
      {open && (
        <div className="layers-panel">
          {defs.map((def) => (
            <label key={def.id}>
              <Checkbox
                checked={visibleIds.includes(def.id)}
                onChange={(checked) => onToggle(def.id, checked)}
              />
              {def.label}
              {loadingIds.includes(def.id) && <span className="loading">{strings.Loading()}…</span>}
            </label>
          ))}
        </div>
      )}
    </LayerTogglesStyled>
  );
};

// Drawing tool

export type DrawingMode = 'rectangle' | 'circle';

const drawingShapeOptions = {
  strokeColor: '#FFFF00',
  strokeOpacity: 0.8,
  strokeWeight: 2,
  fillColor: '#FFFF00',
  fillOpacity: 0.15,
  clickable: false,
};

/** Defining geometry of a drawn region, so callers can persist and redraw it as a native Rectangle/Circle. */
export type DrawnShape =
  | { type: 'rectangle', bounds: { north: number, south: number, east: number, west: number } }
  | { type: 'circle', center: { lat: number, lng: number }, radius: number };

interface DrawingToolProps {
  map: google.maps.Map;
  mode: DrawingMode;
  onComplete: (polygon: Polygon, shape: DrawnShape) => void;
}

/** Right-click drag-to-draw tool that emits a rectangle or circle as a Polygon when finished. */
export const DrawingTool = ({ map, mode, onComplete }: DrawingToolProps) => {
  const drawingRef = useRef({ isDrawing: false, origin: null as google.maps.LatLngLiteral | null });
  const shapeRef = useRef<{ bounds?: google.maps.LatLngBoundsLiteral; center?: google.maps.LatLngLiteral; radius?: number }>(undefined);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const [shape, setShape] = useState<{ bounds?: google.maps.LatLngBoundsLiteral; center?: google.maps.LatLngLiteral; radius?: number }>();
  shapeRef.current = shape;

  useEffect(() => {
    const rightClick = map.addListener('rightclick', (e: google.maps.MapMouseEvent) => {
      if (!e.latLng) return;
      const ref = drawingRef.current;
      if (!ref.isDrawing) {
        ref.isDrawing = true;
        ref.origin = e.latLng.toJSON();
      }
      else {
        ref.isDrawing = false;
        ref.origin = null;
        const s = shapeRef.current;
        if (modeRef.current === 'rectangle' && s?.bounds) {
          onCompleteRef.current(rectangleBoundsToPolygon(s.bounds), { type: 'rectangle', bounds: s.bounds });
        }
        else if (modeRef.current === 'circle' && s?.center && s?.radius) {
          onCompleteRef.current(circleToPolygon(s.center, s.radius), { type: 'circle', center: s.center, radius: s.radius });
        }
        setShape(undefined);
      }
    });
    const mouseMove = map.addListener('mousemove', (e: google.maps.MapMouseEvent) => {
      const ref = drawingRef.current;
      if (!ref.isDrawing || !ref.origin || !e.latLng) return;
      const point = e.latLng.toJSON();
      if (modeRef.current === 'rectangle') {
        setShape({
          bounds: {
            north: Math.max(ref.origin.lat, point.lat),
            south: Math.min(ref.origin.lat, point.lat),
            east: Math.max(ref.origin.lng, point.lng),
            west: Math.min(ref.origin.lng, point.lng),
          },
        });
      }
      else {
        const radius = google.maps.geometry.spherical.computeDistanceBetween(
          new google.maps.LatLng(ref.origin), e.latLng
        );
        setShape({ center: ref.origin, radius });
      }
    });
    return () => { rightClick.remove(); mouseMove.remove(); };
  }, [map]);

  useEffect(() => {
    if (!drawingRef.current.isDrawing) return;
    const prevent = (e: Event) => e.preventDefault();
    document.addEventListener('contextmenu', prevent);
    return () => document.removeEventListener('contextmenu', prevent);
  });

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && drawingRef.current.isDrawing) {
        drawingRef.current.isDrawing = false;
        drawingRef.current.origin = null;
        setShape(undefined);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return <>
    {shape?.bounds && <Rectangle options={drawingShapeOptions} bounds={shape.bounds} />}
    {shape?.center && shape?.radius && <Circle options={drawingShapeOptions} center={shape.center} radius={shape.radius} />}
  </>;
};

/**
 * Converts rectangle bounds into a closed GeoJSON Polygon.
 * @param bounds - north/south/east/west extents
 * @returns the equivalent Polygon
 */
const rectangleBoundsToPolygon = (bounds: google.maps.LatLngBoundsLiteral): Polygon => {
  const { north, south, east, west } = bounds;
  return {
    type: 'Polygon',
    coordinates: [[[east, north], [west, north], [west, south], [east, south], [east, north]]],
  };
};

/**
 * Approximates a circle as a 36-sided GeoJSON Polygon.
 * @param center - circle center
 * @param radius - radius in meters
 * @returns the approximating Polygon
 */
const circleToPolygon = (center: google.maps.LatLngLiteral, radius: number): Polygon => {
  const numSides = 36;
  const degreeStep = 360 / numSides;
  const points: [number, number][] = [];
  for (let i = 0; i < numSides; i++) {
    const gpos = google.maps.geometry.spherical.computeOffset(center, radius, degreeStep * i);
    points.push([gpos.lng(), gpos.lat()]);
  }
  points.push(points[0]);
  return { type: 'Polygon', coordinates: [points] };
};

// Measurement tool

/** A shape drawn to be measured: a closed ring (area and perimeter) or an open path (length only). */
export interface MapMeasurement {
  closed: boolean;
  /** For a closed shape, the ring's vertices with no repeat of the first one at the end. */
  path: google.maps.LatLngLiteral[];
}

export interface MeasurementMetrics {
  lengthFeet: number;
  lengthMiles: number;
  areaSquareFeet?: number;
  areaAcres?: number;
  areaSquareMiles?: number;
}

const FEET_PER_METER = 3.280839895013123;
const MILES_PER_METER = 0.000621371192237334;
const SQUARE_FEET_PER_SQUARE_METER = 10.763910416709722;
const ACRES_PER_SQUARE_METER = 0.000247105381467165;
const SQUARE_MILES_PER_SQUARE_METER = 0.000000386102158542;
// Square feet leads either way; past a few acres square miles are added, as acres alone stop placing it.
const ACRES_BEFORE_WIDE_UNITS = 10;

const measurementStyle = {
  strokeColor: '#ffea00', strokeOpacity: 0.95, strokeWeight: 3,
  fillColor: '#ffea00', fillOpacity: 0.2, zIndex: 6,
};
const selectedMeasurementStyle = { ...measurementStyle, strokeColor: '#ff6d00', fillColor: '#ff6d00', zIndex: 7 };
const draftMeasurementStyle = {
  strokeColor: '#ffea00', strokeOpacity: 0.9, strokeWeight: 2, clickable: false, zIndex: 8,
};

/**
 * Measures a drawn shape on the sphere: the length of its path, plus the area it encloses when closed.
 * @param measurement - the drawn shape
 * @returns length in feet and miles, and for a closed shape the area in square feet, acres and square miles
 */
export const measurementMetrics = (measurement: MapMeasurement): MeasurementMetrics => {
  const { closed, path } = measurement;
  const { spherical } = google.maps.geometry;
  const lengthMeters = spherical.computeLength(closed && path.length ? [...path, path[0]] : path);
  const metrics: MeasurementMetrics = {
    lengthFeet: lengthMeters * FEET_PER_METER,
    lengthMiles: lengthMeters * MILES_PER_METER,
  };
  if (!closed) return metrics;
  const areaSquareMeters = spherical.computeArea(path);
  metrics.areaSquareFeet = areaSquareMeters * SQUARE_FEET_PER_SQUARE_METER;
  metrics.areaAcres = areaSquareMeters * ACRES_PER_SQUARE_METER;
  metrics.areaSquareMiles = areaSquareMeters * SQUARE_MILES_PER_SQUARE_METER;
  return metrics;
};

/**
 * Renders a measurement for the readout, area first where there is one.
 * @param measurement - the drawn shape
 * @returns one line of text per figure
 */
const measurementLines = (measurement: MapMeasurement) => {
  const { lengthFeet, lengthMiles, areaSquareFeet, areaAcres, areaSquareMiles } = measurementMetrics(measurement);
  const length = strings.MeasurementLength(formatNumber(lengthFeet), formatNumber(lengthMiles, 2));
  if (areaSquareFeet === undefined || areaAcres === undefined || areaSquareMiles === undefined) return [length];
  const area = areaAcres < ACRES_BEFORE_WIDE_UNITS
    ? strings.MeasurementArea(formatNumber(areaSquareFeet), formatNumber(areaAcres, 2))
    : strings.MeasurementAreaWide(formatNumber(areaSquareFeet), formatNumber(areaAcres), formatNumber(areaSquareMiles, 2));
  return [area, length];
};

/**
 * Drops vertices that repeat the one before them: a shape closed with a double-click takes its last
 * vertex from the cursor, which may be sitting on the vertex that was just clicked.
 * @param path - the vertices as drawn
 * @returns the path without consecutive duplicates
 */
const withoutRepeatedVertices = (path: google.maps.LatLngLiteral[]) => path.filter((point, i) => {
  const previous = path[i - 1];
  return !previous || Math.abs(previous.lat - point.lat) > 1e-7 || Math.abs(previous.lng - point.lng) > 1e-7;
});

interface MeasurementToolProps {
  map: google.maps.Map;
  measurements: MapMeasurement[];
  onChange: (measurements: MapMeasurement[]) => void;
}

/**
 * Draw-to-measure tool: click vertices to trace a shape, double-click to close it, and read the length
 * and area off the panel. Finished shapes stay on the map, and clicking one shows its figures again.
 */
export const MeasurementTool = ({ map, measurements, onChange }: MeasurementToolProps) => {
  const [measuring, setMeasuring] = useState(false);
  const [draft, setDraft] = useState<google.maps.LatLngLiteral[]>([]);
  const [cursor, setCursor] = useState<google.maps.LatLngLiteral>();
  const [selectedIndex, setSelectedIndex] = useState<number>();
  // The map listeners are registered once per measuring session, so they read the live values off refs.
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const cursorRef = useRef(cursor);
  cursorRef.current = cursor;
  const measurementsRef = useRef(measurements);
  measurementsRef.current = measurements;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const finish = useCallback((closed: boolean, lastVertex?: google.maps.LatLngLiteral) => {
    const drawn = draftRef.current;
    const path = withoutRepeatedVertices(lastVertex ? [...drawn, lastVertex] : drawn);
    setDraft([]);
    setCursor(undefined);
    if (path.length < (closed ? 3 : 2)) return;
    setSelectedIndex(measurementsRef.current.length);
    onChangeRef.current([...measurementsRef.current, { closed, path }]);
  }, []);

  useEffect(() => {
    if (!measuring) return;
    measuringMaps.add(map);
    map.setOptions({ draggableCursor: 'crosshair', disableDoubleClickZoom: true });
    const listeners = [
      map.addListener('click', (e: google.maps.MapMouseEvent) => {
        const point = e.latLng?.toJSON();
        if (point) setDraft((points) => [...points, point]);
      }),
      map.addListener('mousemove', (e: google.maps.MapMouseEvent) => {
        const point = e.latLng?.toJSON();
        if (point) setCursor(point);
      }),
      map.addListener('dblclick', () => finish(true, cursorRef.current)),
    ];
    return () => {
      measuringMaps.delete(map);
      map.setOptions({ draggableCursor: null, disableDoubleClickZoom: false });
      for (const listener of listeners) listener.remove();
      setDraft([]);
      setCursor(undefined);
    };
  }, [measuring, map, finish]);

  useEffect(() => {
    if (!measuring) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setDraft([]);
      setCursor(undefined);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [measuring]);

  // A click on open map clears the selection; a click on a shape is the shape's own and never reaches here.
  useEffect(() => {
    if (measuring || selectedIndex === undefined) return;
    const listener = map.addListener('click', () => setSelectedIndex(undefined));
    return () => listener.remove();
  }, [map, measuring, selectedIndex]);

  const draftPath = cursor ? [...draft, cursor] : draft;
  const selected = selectedIndex === undefined ? undefined : measurements[selectedIndex];
  const lines = draft.length > 0
    ? measurementLines({ closed: false, path: draftPath })
    : selected ? measurementLines(selected) : [];
  return <>
    {measurements.map((measurement, index) => {
      const style = { ...(index === selectedIndex ? selectedMeasurementStyle : measurementStyle), clickable: !measuring };
      const select = () => setSelectedIndex(index);
      return measurement.closed
        ? <PolygonShape key={index} path={measurement.path} options={style} onClick={select} />
        : <Polyline key={index} path={measurement.path} options={style} onClick={select} />;
    })}
    {draft.length > 0 && <Polyline path={draftPath} options={draftMeasurementStyle} />}
    <MapControl map={map} position="LEFT_TOP">
      <MeasurementPanelStyled>
        <button
          className={`measure-toggle${measuring ? ' active' : ''}`}
          aria-label={strings.Measure()}
          data-tooltip-id="measure-toggle-tip"
          data-tooltip-content={strings.Measure()}
          data-tooltip-place="right"
          onClick={() => { setMeasuring(!measuring); setSelectedIndex(undefined); }}
        >
          <Icon icon="squareFoot" size={22} fill={measuring ? css.white : css.grey3} />
          {measurements.length > 0 && <span className="measure-count">{measurements.length}</span>}
        </button>
        <Tooltip id="measure-toggle-tip" />
        {(measuring || lines.length > 0) && (
          <div className="measure-panel">
            {measuring && (
              <div className="measure-actions">
                <button disabled={draft.length < 2} onClick={() => finish(false)}>{strings.Done()}</button>
                <button disabled={!draft.length} onClick={() => setDraft((points) => points.slice(0, -1))}>{strings.Undo()}</button>
              </div>
            )}
            {lines.map((line) => <div key={line} className="measure-readout">{line}</div>)}
            {!draft.length && selected && (
              <button onClick={() => {
                onChange(measurements.filter((_, index) => index !== selectedIndex));
                setSelectedIndex(undefined);
              }}>{strings.Delete()}</button>
            )}
            {measuring && <div className="measure-hint">{strings.MeasureHint()}</div>}
          </div>
        )}
      </MeasurementPanelStyled>
    </MapControl>
  </>;
};

const MeasurementPanelStyled = styled.div`
  margin: 10px;
  button {
    background: ${css.white};
    border: none;
    border-radius: 2px;
    padding: 0.5rem 0.75rem;
    cursor: pointer;
    font-size: 1.05rem;
    font-weight: 500;
    &:hover {
      background: #f5f5f5;
    }
    &:disabled {
      opacity: 0.5;
      cursor: default;
    }
    &.active {
      background: ${css.primaryColor};
      color: ${css.white};
    }
  }
  /* Sized to sit with Google's own 40px map controls. */
  .measure-toggle {
    min-width: 40px;
    height: 40px;
    padding: 0 9px;
    display: flex;
    align-items: center;
    gap: 0.25rem;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
  }
  .measure-count {
    font-size: 0.9rem;
    font-weight: 600;
  }
  .measure-panel {
    margin-top: 1px;
    background: rgba(255, 255, 255, 0.95);
    border-radius: 2px;
    padding: 0.5rem 0.75rem;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.35rem;
    white-space: nowrap;
  }
  .measure-actions {
    display: flex;
    gap: 0.35rem;
  }
  .measure-readout {
    font-size: 1rem;
    font-weight: 600;
  }
  .measure-hint {
    font-size: 0.8rem;
    white-space: normal;
    max-width: 14rem;
    color: ${css.grey3};
  }
`;

/**
 * Ray-casting test for whether a lng/lat point falls inside a single ring.
 * Declared as a function so it hoists for use earlier in the module.
 * @param lng - point longitude
 * @param lat - point latitude
 * @param ring - ring as an array of [lng, lat] pairs
 * @returns true if the point is inside the ring
 */
function pointInRing(lng: number, lat: number, ring: number[][]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0], yi = ring[i][1];
    const xj = ring[j][0], yj = ring[j][1];
    const intersect = ((yi > lat) !== (yj > lat)) && (lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

/**
 * Point-in-polygon test honoring holes (rings[0] is outer, the rest are holes).
 * @param lng - point longitude
 * @param lat - point latitude
 * @param rings - polygon rings, outer first
 * @returns true if inside the outer ring and outside all holes
 */
function pointInPolygonRings(lng: number, lat: number, rings: number[][][]): boolean {
  if (!rings?.length) return false;
  if (!pointInRing(lng, lat, rings[0])) return false;
  for (let k = 1; k < rings.length; k++) if (pointInRing(lng, lat, rings[k])) return false;
  return true;
}

/** How close a click has to land on a point or a line to count, in pixels. */
const CLICK_TOLERANCE_PIXELS = 6;

/**
 * Converts the click tolerance to degrees at the map's current zoom, since that is what the raw
 * GeoJSON coordinates are in.
 * @param map - the map being clicked
 * @returns the tolerance in degrees
 */
const clickToleranceDegrees = (map: google.maps.Map) => {
  const zoom = map.getZoom() ?? 12;
  return (360 / (256 * 2 ** zoom)) * CLICK_TOLERANCE_PIXELS;
};

/**
 * Squared distance from a point to a segment, in degrees.
 * @param lng - point longitude
 * @param lat - point latitude
 * @param from - segment start as [lng, lat]
 * @param to - segment end as [lng, lat]
 * @returns the squared distance
 */
function squaredDistanceToSegment(lng: number, lat: number, from: number[], to: number[]): number {
  const dx = to[0] - from[0];
  const dy = to[1] - from[1];
  const lengthSquared = dx * dx + dy * dy;
  const t = lengthSquared ? Math.max(0, Math.min(1, ((lng - from[0]) * dx + (lat - from[1]) * dy) / lengthSquared)) : 0;
  const nx = from[0] + t * dx - lng;
  const ny = from[1] + t * dy - lat;
  return nx * nx + ny * ny;
}

/**
 * Tests whether a point falls within the tolerance of a line's path.
 * @param lng - point longitude
 * @param lat - point latitude
 * @param line - the line as [lng, lat] pairs
 * @param tolerance - how near counts, in degrees
 * @returns true if the point is on or near the line
 */
function nearLine(lng: number, lat: number, line: number[][], tolerance: number): boolean {
  for (let i = 1; i < (line?.length || 0); i++) {
    if (squaredDistanceToSegment(lng, lat, line[i - 1], line[i]) <= tolerance * tolerance) return true;
  }
  return false;
}

/**
 * Hit test for a GeoJSON geometry: inside, for an area; within the tolerance, for a point or a line.
 * @param lng - point longitude
 * @param lat - point latitude
 * @param geometry - GeoJSON geometry
 * @param tolerance - how near counts for points and lines, in degrees
 * @returns true if the click belongs to the geometry
 */
function pointInGeometry(lng: number, lat: number, geometry: any, tolerance = 0): boolean {
  if (!geometry) return false;
  if (geometry.type === 'Polygon') return pointInPolygonRings(lng, lat, geometry.coordinates);
  if (geometry.type === 'MultiPolygon') return (geometry.coordinates as number[][][][]).some((poly) => pointInPolygonRings(lng, lat, poly));
  if (geometry.type === 'Point') {
    const [x, y] = geometry.coordinates || [];
    return Math.abs(x - lng) <= tolerance && Math.abs(y - lat) <= tolerance;
  }
  if (geometry.type === 'LineString') return nearLine(lng, lat, geometry.coordinates, tolerance);
  if (geometry.type === 'MultiLineString') return (geometry.coordinates as number[][][]).some((line) => nearLine(lng, lat, line, tolerance));
  return false;
}
